import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { socialPlatformLabel } from '@mg/shared';
import type { Post, ResolveThreadPostDto } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { useDialogs } from '../../components/ui/DialogProvider';
import { threadUnconfirmedText } from './thread';

/**
 * Everything that shows a post's thread - the post, the post lists, the campaign page and list, the
 * set's tabs - under whatever workspace key each is cached. A decision changes what all of them say.
 */
const SHOWS_THE_THREAD = ['post', 'posts', 'campaign', 'campaigns', 'post-group'];

/**
 * A thread stopped on a post whose send was started and never confirmed: the app lost contact with
 * the platform while sending it, so it does not know whether that post went out. Nothing more of
 * the thread is sent until a person has looked and says which - sending it again on the app's own
 * guess could post it twice.
 *
 * "It's already live" takes the link to that post, so the next post can reply to it. "It didn't go
 * out" lets Retry send it. Either is the server's to accept: a refusal is shown in its own words
 * and nothing changes.
 *
 * Self-contained - the post card and the composer both draw it - and it draws nothing for a post
 * that is not waiting for a decision.
 */
export function ThreadUnconfirmedNotice({
  post,
}: {
  post: Pick<Post, 'id' | 'platform' | 'thread'>;
}) {
  const queryClient = useQueryClient();
  const { confirm, prompt } = useDialogs();
  const [refusal, setRefusal] = useState<string | null>(null);

  const resolve = useMutation({
    mutationFn: (dto: ResolveThreadPostDto) => api.resolveThreadPost(post.id, dto),
    onMutate: () => setRefusal(null),
    onSuccess: () => {
      for (const prefix of SHOWS_THE_THREAD) {
        void queryClient.invalidateQueries({ queryKey: [prefix] });
      }
    },
    onError: (err) =>
      setRefusal(
        err instanceof ApiRequestError ? err.message : 'Could not save that — please try again.',
      ),
  });

  const thread = post.thread;
  if (!thread?.unconfirmed) return null;

  /** The post in question: the one after those that are live. */
  const n = thread.published + 1;
  const where = post.platform ? socialPlatformLabel[post.platform] : 'the platform';
  /**
   * The attempt this notice is about, as drawn - an answer is about what the person checked, so it is
   * sent with the answer even if the page has moved on while a dialog was open (the server then refuses
   * it). Empty only from a server older than the attempt id, which refuses any id.
   */
  const attemptId = thread.unconfirmedAttemptId ?? '';

  const alreadyLive = async () => {
    const url = await prompt({
      title: `Link to post ${n}`,
      message: `Open post ${n} on ${where}, copy its link and paste it here.`,
      label: 'Link',
      placeholder: 'https://',
      confirmLabel: 'Save link',
    });
    if (url) resolve.mutate({ decision: 'adopt', url, attemptId });
  };

  const didNotGoOut = async () => {
    const sure = await confirm({
      title: `Post ${n} didn't go out?`,
      message: `Only choose this if post ${n} is not on ${where}. Retry will send it, and if it is there after all it will appear twice.`,
      confirmLabel: "It didn't go out",
      danger: true,
    });
    if (sure) resolve.mutate({ decision: 'resend', attemptId });
  };

  return (
    <div className="rounded-lg border border-amber-200 dark:border-warning-dark/30 bg-amber-50 dark:bg-warning-dark-tint px-3 py-2 text-sm text-amber-800 dark:text-warning-dark">
      <p>{threadUnconfirmedText(post.platform, thread)}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          loading={resolve.isPending && resolve.variables?.decision === 'adopt'}
          disabled={resolve.isPending}
          onClick={() => void alreadyLive()}
        >
          It&apos;s already live
        </Button>
        <Button
          variant="secondary"
          size="sm"
          loading={resolve.isPending && resolve.variables?.decision === 'resend'}
          disabled={resolve.isPending}
          onClick={() => void didNotGoOut()}
        >
          It didn&apos;t go out
        </Button>
      </div>
      {refusal && <p className="mt-1.5 text-xs text-red-600 dark:text-error-dark">{refusal}</p>}
    </div>
  );
}
